import { useState, useEffect, useCallback, useRef } from 'react';
import { dataAPI } from '../services/api';
import { categoryOf, isHiddenSystemItem } from '../utils/itemTypes';

/**
 * Lista ligera de items del vault para el dashboard nuevo (tabla + "Mas usadas").
 *
 * Es una version limpia y reducida de la carga que hace components/Vault.js
 * (fetchItems + mapRawData, ~l.406-740): mismo endpoint (`GET /data/:rfidId?light=1`),
 * mismo contrato de paginacion (page/limit/totalPages/hasMore), pero sin todo el
 * estado de edicion/seleccion/sincronizacion que carga Vault.js. Se duplica a
 * proposito en vez de extraerla de Vault.js: tocar ese fichero para compartir
 * codigo es mas riesgo que las ~40 lineas que se repiten aqui.
 *
 * No expone contraseñas en claro: igual que el modo `light` de Vault.js, el
 * campo `password` llega vacio y el detalle completo se pide aparte con
 * dataAPI.get(id, rfidId) solo cuando el usuario abre el item.
 *
 * El filtro (type/tag/vault/search) se aplica en el servidor (ver GET
 * /data/:rfidId), así que "cargar más" trae siempre página nueva, nunca
 * depende de lo que haya en memoria.
 */
function mapItem(item) {
  let parsed = {};
  try {
    if (item.data && typeof item.data === 'string') parsed = JSON.parse(item.data);
    else if (item.data && typeof item.data === 'object') parsed = item.data;
  } catch (_) { /* dato corrupto o parcial: se trata como vacio */ }

  return {
    id: item._id || item.id,
    title: item.name || item.title || '',
    type: parsed.type || 'note',
    category: categoryOf(parsed.type),
    // Passkey no tiene username/url: la fila usa rpId como subtítulo y
    // origin (o el propio rpId, que ya es un dominio) para el favicon.
    username: parsed.username || parsed.email || (parsed.type === 'passkey' ? parsed.rpId : '') || '',
    url: parsed.url || (parsed.type === 'passkey' ? (parsed.origin || parsed.rpId) : '') || '',
    vault: parsed.vault || 'personal',
    favorite: !!parsed.favorite,
    tags: item.tags || parsed.tags || [],
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

export function useVaultItems({ page = 1, limit = 20, type = null, tag = null, vault = null, search = null } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [cardName, setCardName] = useState('');
  const pageRef = useRef(page);

  const rfidId = typeof window !== 'undefined' ? localStorage.getItem('rfidId') : null;

  const fetchPage = useCallback(async (targetPage, { append } = {}) => {
    if (!rfidId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const opts = { page: targetPage, limit };
      if (type === 'favorites') opts.favorite = '1';
      else if (type && type !== 'all') opts.type = type;
      if (tag) opts.tag = tag;
      if (vault && vault !== 'all') opts.vault = vault;
      if (search) opts.search = search;

      const res = await dataAPI.list(rfidId, opts);
      const raw = (res.data?.data || []).filter((it) => {
        const name = it.name || it.title || '';
        const t = (typeof it.data === 'object' ? it.data?.type : null);
        return !isHiddenSystemItem({ name, type: t });
      });
      const mapped = raw.map(mapItem);

      setItems((prev) => (append ? [...prev, ...mapped] : mapped));
      setTotalItems(res.data?.totalCount ?? mapped.length);
      setTotalPages(res.data?.totalPages ?? 1);
      setHasMore(!!res.data?.hasMore);
      setCardName(res.data?.cardName || '');
      pageRef.current = targetPage;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, type, tag, vault, search, rfidId]);

  const reload = useCallback(() => fetchPage(1, { append: false }), [fetchPage]);
  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchPage(pageRef.current + 1, { append: true });
  }, [fetchPage, hasMore, loading]);

  useEffect(() => { reload(); }, [reload]);

  return { items, loading, error, totalItems, totalPages, hasMore, loadMore, cardName, reload };
}

export default useVaultItems;
